/**
 * Vendor Verification API — the PLATFORM review surface (M08).
 *
 * A platform verifier is NOT a member of the tenant being reviewed, so the tenant
 * is named with the `X-Tenant-Id` SELECTOR header rather than resolved from a
 * membership. The server re-checks the platform grant `tenant.verification.review`
 * and the tenant's existence inside the database, and writes one `audit_log` row
 * per review read.
 *
 * CONTRACT SOURCE: contracts/openapi/vendor-verification.v1.yaml.
 * Every response is parsed by its Zod schema at the transport boundary.
 */
import { http, generateIdempotencyKey } from '@/api/client/http';
import {
  VerificationCaseResponseSchema,
  VerificationCaseReviewResponseSchema,
} from '@/api/schemas/vendor-verification';
import type {
  DecideVerificationCaseRequest,
  VerificationCase,
  VerificationCaseReview,
} from '@/api/schemas/vendor-verification';

export const VerificationReviewApi = {
  /**
   * GET /vendor/verification-cases/{caseId}/review — the VERIFIER projection.
   *
   * Releases the three account identifiers the vendor projection withholds, and
   * writes one `audit_log` row. Requires `tenant.verification.review`.
   */
  async getReview(tenantId: string, caseId: string): Promise<VerificationCaseReview> {
    const response = await http.get(
      `/vendor/verification-cases/${caseId}/review`,
      VerificationCaseReviewResponseSchema,
      { tenantId },
    );
    return response.data;
  },

  /**
   * POST /vendor/verification-cases/{caseId}/decision — approve / reject / request
   * more evidence.
   *
   * `If-Match` carries the case `version` (bare integer form). `Idempotency-Key`
   * is required. Separation of duties is enforced server-side: the submitter
   * cannot decide their own case.
   */
  async decide(
    tenantId: string,
    caseId: string,
    expectedVersion: number,
    input: DecideVerificationCaseRequest,
  ): Promise<VerificationCase> {
    const response = await http.post(
      `/vendor/verification-cases/${caseId}/decision`,
      VerificationCaseResponseSchema,
      {
        body: input,
        tenantId,
        ifMatch: expectedVersion,
        ifMatchFormat: 'bare-version',
        idempotencyKey: generateIdempotencyKey(),
      },
    );
    return response.data;
  },
};

export type { DecideVerificationCaseRequest, VerificationCase, VerificationCaseReview };
